import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Users as UsersIcon, ShieldCheck } from "lucide-react";

const ROLES = [
  { id: "superadmin",         label: "Super Admin",        desc: "Global · full CRUD across all orgs" },
  { id: "org_admin",          label: "Org Admin",          desc: "CRUD within assigned org(s)" },
  { id: "org_viewer",         label: "Org Viewer",         desc: "Read-only within assigned org(s)" },
  { id: "pub_admin",          label: "Publication Admin",  desc: "CRUD within assigned publication house(s)" },
  { id: "pub_viewer",         label: "Publication Viewer", desc: "Read-only within assigned publication house(s)" },
  { id: "periodical_admin",   label: "Periodical Admin",   desc: "CRUD on assigned periodical(s)" },
  { id: "periodical_viewer",  label: "Periodical Viewer",  desc: "Read-only on assigned periodical(s)" },
];

const ROLE_LABEL = Object.fromEntries(ROLES.map(r => [r.id, r.label]));

export default function UsersList() {
  const { user: me, isSuperAdmin, enterViewAs } = useAuth();
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);  // user obj or { __new: true }
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users", { params: search ? { search } : {} });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onDelete = async () => {
    try {
      await api.delete(`/users/${deleting.id}`);
      toast.success("User deleted");
      setDeleting(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const onViewAs = async (u) => {
    await enterViewAs(u.id);
    toast.success(`Now viewing as ${u.email} (read-only)`);
  };

  if (!isSuperAdmin) {
    return (
      <div className="rounded-sm border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-700" data-testid="users-forbidden">
        Only SuperAdmin can manage users.
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">RBAC · Users</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <UsersIcon size={26} className="text-[#002FA7]" />
            Users &amp; Permissions
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">Create role-scoped logins for org / publication / periodical teams.</p>
        </div>
        <Button data-testid="user-new-btn" onClick={() => setEditing({ __new: true })} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
          <Plus size={14} className="mr-1.5" /> New user
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Input
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="max-w-md rounded-sm"
          data-testid="users-search"
        />
        <Button variant="outline" onClick={load} className="rounded-sm">Search</Button>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white">
        <Table className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Scopes</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items === null && (
              <TableRow><TableCell colSpan={5}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            )}
            {items?.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">No users found.</TableCell></TableRow>
            )}
            {items?.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-mono text-xs text-slate-700">{u.email}</TableCell>
                <TableCell className="text-sm">{u.name || "—"}</TableCell>
                <TableCell><span className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px]">{ROLE_LABEL[u.role] || u.role}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{u.role === "superadmin" ? <span className="italic">Global</span> : `${(u.scopes || []).length} scope(s)`}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {u.id !== me?.id && u.role !== "superadmin" && (
                    <Button size="icon" variant="ghost" title="View as this user (read-only)" onClick={() => onViewAs(u)} data-testid={`user-viewas-${u.id}`} className="h-8 w-8 rounded-sm text-amber-600 hover:bg-amber-50">
                      <Eye size={16} />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" title="Edit" onClick={() => setEditing(u)} data-testid={`user-edit-${u.id}`} className="h-8 w-8 rounded-sm text-[#002FA7] hover:bg-indigo-50">
                    <Pencil size={16} />
                  </Button>
                  {u.id !== me?.id && (
                    <Button size="icon" variant="ghost" title="Delete" onClick={() => setDeleting(u)} data-testid={`user-delete-${u.id}`} className="h-8 w-8 rounded-sm text-rose-600 hover:bg-rose-50">
                      <Trash2 size={16} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && <UserForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="user-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Delete user?</DialogTitle>
            <DialogDescription>
              {deleting && `${deleting.email} will lose access immediately. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={onDelete} className="rounded-sm bg-rose-600 hover:bg-rose-700" data-testid="user-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Form --------
function UserForm({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [email, setEmail] = useState(initial.email || "");
  const [name, setName] = useState(initial.name || "");
  const [role, setRole] = useState(initial.role || "org_viewer");
  const [password, setPassword] = useState("");
  const [scopes, setScopes] = useState(initial.scopes || []);
  const [submitting, setSubmitting] = useState(false);

  const [orgs, setOrgs] = useState([]);
  const [pubsByOrg, setPubsByOrg] = useState({});
  const [persByPub, setPersByPub] = useState({});

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/organizations", { params: { include_disabled: false } });
      setOrgs(data);
    } catch { /* ignore */ }
  })(); }, []);

  const fetchPubs = async (orgId) => {
    if (pubsByOrg[orgId]) return;
    const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } });
    setPubsByOrg((p) => ({ ...p, [orgId]: data }));
  };
  const fetchPers = async (pubId) => {
    if (persByPub[pubId]) return;
    const { data } = await api.get("/periodicals", { params: { publication_house_id: pubId } });
    setPersByPub((p) => ({ ...p, [pubId]: data.items || [] }));
  };

  const addScope = () => setScopes((s) => [...s, { organization_id: "" }]);
  const removeScope = (i) => setScopes((s) => s.filter((_, idx) => idx !== i));
  const updateScope = (i, field, value) => setScopes((s) => {
    const next = s.slice();
    next[i] = { ...next[i], [field]: value };
    // Clear deeper levels when a higher level changes
    if (field === "organization_id") { delete next[i].publication_house_id; delete next[i].periodical_id; }
    if (field === "publication_house_id") delete next[i].periodical_id;
    return next;
  });

  const requiredDepth = useMemo(() => {
    if (role === "superadmin") return "none";
    if (role.startsWith("org")) return "org";
    if (role.startsWith("pub")) return "pub";
    return "periodical";
  }, [role]);

  const submit = async () => {
    if (isNew && !password) { toast.error("Password is required"); return; }
    if (role !== "superadmin" && scopes.length === 0) { toast.error("Add at least one scope"); return; }
    setSubmitting(true);
    try {
      const cleanScopes = role === "superadmin" ? [] : scopes
        .filter((s) => s.organization_id)
        .map((s) => ({
          organization_id: s.organization_id,
          publication_house_id: s.publication_house_id || null,
          periodical_id: s.periodical_id || null,
        }));
      const payload = { email, name, role, scopes: cleanScopes };
      if (password) payload.password = password;
      if (isNew) await api.post("/users", payload);
      else await api.put(`/users/${initial.id}`, payload);
      toast.success(isNew ? "User created" : "User updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#002FA7]" />{isNew ? "Create user" : "Edit user"}</DialogTitle>
          <DialogDescription>Assign a role and the exact scope(s) this user can access. Scope is enforced server-side.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm">Email <span className="text-rose-600">*</span></Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!isNew} className="rounded-sm" data-testid="user-email-input" />
          </div>
          <div>
            <Label className="text-sm">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="user-name-input" />
          </div>
          <div>
            <Label className="text-sm">Role <span className="text-rose-600">*</span></Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{r.label}</span>
                      <span className="text-[11px] text-slate-500">{r.desc}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">{isNew ? "Password" : "Reset password"} {isNew && <span className="text-rose-600">*</span>}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isNew ? "" : "Leave blank to keep"} className="rounded-sm" data-testid="user-password-input" />
          </div>
        </div>

        {role !== "superadmin" && (
          <div className="mt-2 rounded-sm border border-slate-200 bg-slate-50/50 p-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Scopes (required: {requiredDepth})</div>
              <Button size="sm" variant="outline" onClick={addScope} className="h-7 rounded-sm" data-testid="user-scope-add"><Plus size={12} className="mr-1" />Add scope</Button>
            </div>
            <div className="mt-2 space-y-2">
              {scopes.length === 0 && <div className="text-xs text-slate-400">No scopes yet — click "Add scope".</div>}
              {scopes.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-sm border border-slate-200 bg-white p-2" data-testid={`scope-row-${i}`}>
                  <div className="col-span-4">
                    <Select value={s.organization_id || ""} onValueChange={(v) => { updateScope(i, "organization_id", v); fetchPubs(v); }}>
                      <SelectTrigger className="rounded-sm text-xs"><SelectValue placeholder="Organization" /></SelectTrigger>
                      <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Select value={s.publication_house_id || ""} disabled={!s.organization_id || requiredDepth === "org"} onValueChange={(v) => { updateScope(i, "publication_house_id", v); fetchPers(v); }}>
                      <SelectTrigger className="rounded-sm text-xs"><SelectValue placeholder={requiredDepth === "org" ? "—" : "Publication House"} /></SelectTrigger>
                      <SelectContent>{(pubsByOrg[s.organization_id] || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Select value={s.periodical_id || ""} disabled={!s.publication_house_id || requiredDepth !== "periodical"} onValueChange={(v) => updateScope(i, "periodical_id", v)}>
                      <SelectTrigger className="rounded-sm text-xs"><SelectValue placeholder={requiredDepth === "periodical" ? "Periodical" : "—"} /></SelectTrigger>
                      <SelectContent>{(persByPub[s.publication_house_id] || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeScope(i)} className="h-7 w-7 rounded-sm text-rose-600 hover:bg-rose-50" data-testid={`scope-remove-${i}`}><Trash2 size={12} /></Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]" data-testid="user-save-btn">
            {submitting ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
